import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SellerInfo } from '../models/seller-info';
import { CoreCollectionResponse } from '../../@core/http/core-response';

export interface SellerCreate {
  seller_first_name?: string;
  seller_last_name?: string;
  seller_email?: string;
  seller_phone?: string;
  seller_social_id?: string;
  seller_bank_code?: number;
  seller_bank_branch?: string;
  seller_bank_account_number?: string;
  language?: string;
}

export interface SellerUpdate {
  seller_first_name?: string;
  seller_last_name?: string;
  seller_email?: string;
  seller_phone?: string;
  seller_social_id?: string;
  seller_bank_code?: number;
  seller_bank_branch?: string;
  seller_bank_account_number?: string;
  seller_merchant_name?: string;
  seller_inc_code?: number;
  seller_address_city?: string;
  seller_address_street?: string;
  seller_address_street_number?: string;
  seller_person_business_type?: number;
  language?: string;
  seller_site_url?: string;
}

export interface PdSellerAgreementData {
  seller_merchant_name: string;
  seller_inc_code: string;
  seller_address: string;
  seller_person_business_type: string;
  seller_phone: string;
  seller_bank_code: string;
  seller_bank_branch: string;
  seller_bank_account_number: string;
  fields: {
    signature: { base64: string };
    business_stamp: { base64: string };
    business_stamp2: { base64: string };
    signature2: { base64: string };
  };
}

export enum FileTypesEnum {
  SocialId = 1,
  BankAccountOwnership = 2,
  CorporateCertificate = 3,
  BankAuthorization = 4,
  PersonalGuarantee = 5,
  PromissoryNote = 6,
}

export interface SellerFile {
  name: string;
  type: FileTypesEnum;
  base64: string;
  mime_type: string;
}

// Probably can be replaced with one FileTypesEnum
export enum VerificationTypeEnum {
  SocialId = 1,
  BankAccountOwnership = 2,
  CorporateCertificate = 3,
  BankAuthorization = 4,
  PersonalGuarantee = 5,
  PromissoryNote = 6,
  ProcessingAgreement = 7,
  Signature = 8,
  Stamp = 9,
  SignatoriesApproval = 10,
  AdditionalLicense = 11,
  PublicRepresentative = 12,
  RegulatoryAuthentication = 13,
  AuthorizedSignerProtocol = 14,
  BusinessProof = 15,
  ServiceReceiver = 16,
  FaceToFaceApproval = 17,
  FalseStatementOfInformation = 18,
  CompanyLogo = 23,
  DrivingLicense = 24,
  SocialIdAppendix = 25,
  Passport = 26,
  OriginTaxConfirmation = 27,
  BookkeepingCertificate = 28,
  Website = 201,
}

export enum VerificationStatusEnum {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
}

export interface SellerValidation {
  type: VerificationTypeEnum;
  status: VerificationStatusEnum;
}

@Injectable()
export class SellerService {
  constructor(private readonly http: HttpClient) {}

  public getSellerInfo(hashedData: string): Observable<SellerInfo> {
    const url = '/api/payment-contracts/financing-options/hashedData/pd';
    return this.http.get<SellerInfo>(url.replace('hashedData', hashedData), {});
  }

  public createSeller(data: SellerCreate) {
    const url = '/api/payment-contracts/client/sellers';
    return this.http.post(url, data);
  }

  public updateSeller(hash: string, data: SellerUpdate) {
    const url = '/api/payment-contracts/financing-options/sellers/{sellerHash}';
    return this.http.put(url.replace('{sellerHash}', hash), data);
  }

  public sendSellerFiles(hash: string, files: SellerFile[]) {
    const url = '/api/payment-contracts/financing-options/sellers/{hashedRecipientData}/files';
    return this.http.post(url.replace('{hashedRecipientData}', hash), { seller_files: files });
  }

  // TODO: may be this method should be a part of another service
  public createPdSellerAgreement(hash: string, data: PdSellerAgreementData) {
    const url = '/api/payment-contracts/financing-options/{hashedRecipientData}/pd/agreements';
    return this.http.post(url.replace('{hashedRecipientData}', hash), data);
  }

  public getSellerValidations(hash: string) {
    const url = '/api/payment-contracts/financing-options/{hashedRecipientData}/validations';
    return this.http.get<CoreCollectionResponse<SellerValidation>>(url.replace('{hashedRecipientData}', hash));
  }
}
